import { Point } from 'logic/reports/doLinesIntersect';
import { DrawData } from 'ui/shared/calculateDrawData';

export const consts = {
    fontSize: 12, // pixels
    dragHandleSize: 9,
    labelSpacing: 7,
};

const drawLabel = (ctx: CanvasRenderingContext2D, ptFrom: Point, ptTo: Point, isIn: boolean, label: string) => {
    // This code was taken from fencesControl.js in ServerConfig... and adapted to just draw the labels (no arrows)

    const midX = ptFrom.x - (ptFrom.x - ptTo.x) / 2;
    const midY = ptFrom.y - (ptFrom.y - ptTo.y) / 2;
    const slope = (ptTo.y - ptFrom.y) / (ptTo.x - ptFrom.x);

    const x = (ptTo.x - ptFrom.x);
    const y = (ptTo.y - ptFrom.y);
    const r = Math.sqrt(x * x + y * y);
    let theta = Math.acos(x / r);
    if (y > 0) {
        theta = 2 * Math.PI - theta;
    }

    // Delta from the fence line to the label base
    let dx = consts.labelSpacing * Math.cos(Math.PI / 2 - theta);
    let dy = consts.labelSpacing * Math.sin(Math.PI / 2 - theta);

    const ptOnLine = {
        x: midX,
        y: midY,
    };

    const labelOrigin = {
        x: midX + dx * (isIn ? -1 : 1),
        y: midY + dy * (isIn ? -1 : 1),
    };


    const { width } = ctx.measureText(label);
    const height = 7; // WTF? measureText only gives the width.

    // Offset the origin by the size of an ellipse approximately around the text
    const a = width / 1.5;
    const b = height / 1.5;
    const normal = 1 / slope;
    theta = Math.atan(normal);
    dx = a * Math.cos(theta);
    dy = b * Math.sin(theta);

    // Adjust for quadrant
    if (ptOnLine.x > labelOrigin.x) {
        dx = -dx;
    }
    else if (slope === 0) { // Need special case for slope = 0 (aka normal = Infinity) because arctan(Infinity) gets confused.
        dy = Math.abs(dy);
        if (ptOnLine.y > labelOrigin.y) {
            dy *= -1;
        }
    }
    else {
        dy = -dy;
    }

    const pt = {
        x: labelOrigin.x + dx - width / 2,
        y: labelOrigin.y + dy + height / 2,
    };

    ctx.save();
    ctx.lineWidth = 3;
    ctx.strokeStyle = '#000';
    ctx.strokeText(label, pt.x, pt.y);
    ctx.lineWidth = 1;
    ctx.fillStyle = '#fff';
    ctx.fillText(label, pt.x, pt.y);
    ctx.restore();
};

export const drawLinesOnCanvas = (
    canvas: HTMLCanvasElement | null,
    drawData: DrawData,
    backgroundImage: HTMLImageElement | null,
    points: ReadonlyArray<Point>,
    selectedIndex: number | null,
    inLabel: string,
    outLabel: string
) => {
    if (!canvas) {
        return;
    }

    const ctx = canvas.getContext('2d');
    if (!ctx) {
        throw new Error('Failed to get 2d context');
    }
    ctx.save();

    ctx.font = `${consts.fontSize}px Arial`;

    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.translate(drawData.xOffset, drawData.yOffset);

    // Draw the background image scaled and centered on the canvas
    if (backgroundImage) {
        ctx.drawImage(backgroundImage, 0, 0, drawData.width, drawData.height);
    }

    ctx.scale(drawData.scale, drawData.scale);

    // Draw the lines on the canvas
    ctx.beginPath();
    points.forEach(({ x, y }, i) => {
        i === 0
            ? ctx.moveTo(x, y)
            : ctx.lineTo(x, y);
    });
    ctx.strokeStyle = 'rgba(0,0,0,.5)';
    ctx.lineWidth = 3;
    ctx.stroke();
    ctx.strokeStyle = '#fff';
    ctx.lineWidth = 1;
    ctx.stroke();

    points.forEach(({ x, y }, i) => {
        if (i > 0) {
            drawLabel(ctx, points[i - 1], points[i], true, inLabel);
            drawLabel(ctx, points[i - 1], points[i], false, outLabel);
        }
    });

    // Draw corner drag handles
    points.forEach(({ x, y }, i) => {
        let offset = (consts.dragHandleSize - 1) / 2;
        let size = consts.dragHandleSize;
        ctx.fillStyle = 'rgba(0,0,0,.5)';
        ctx.fillRect(x - offset, y - offset, size, size);

        offset--;
        size -= 2;
        ctx.fillStyle = '#fff';
        ctx.fillRect(x - offset, y - offset, size, size);

        if (i !== selectedIndex) {
            offset--;
            size -= 2;
            ctx.fillStyle = '#000';
            ctx.fillRect(x - offset, y - offset, size, size);
        }
    });

    ctx.restore();
};



// WEBPACK FOOTER //
// ./src/ui/pages/designer/sections/line/drawLinesOnCanvas.ts